import { ConfigProvider, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { GetTeamMemberInstancesRes } from '@/api/type-response'
import { ServerDomainInfo } from '@/api/type-server'
import { Checkbox, Divider, message, Modal, Trans } from '@/components'
import { ActionMenu } from '@/components/base/ActionMenu'
import { useTeamStore } from '@/stores/team'
import { formatDomain, formatPrice } from '@/utils/formatter'
import { cn } from '@/utils/style'

export interface AdminInstance {
  id: number
  resID: string
  displayTitle: string
  domain: number
  owner: {
    resID: string
    name: string
    nickname: string
  }
  statusKey: string
  statusText: string
  statusTextStyle: string
  cost: number
  canRelease: boolean
  _fullData?: GetTeamMemberInstancesRes['instances'][0] // Full API response data
}

interface AdminInstanceTableProps {
  instances: AdminInstance[]
  domainInfos: ServerDomainInfo[]
  selectedIds: string[]
  onSelectionChange: (ids: string[]) => void
  onUpdate: () => void
}

export function AdminInstanceTable({
  instances,
  domainInfos,
  selectedIds,
  onSelectionChange,
  onUpdate,
}: AdminInstanceTableProps) {
  const { t } = useTranslation()
  const teamStore = useTeamStore()
  const [sortedInfo, setSortedInfo] = useState<any>({})

  const allIds = instances.map((ins) => ins.resID)
  const isAllSelected = allIds.length > 0 && selectedIds.length === allIds.length
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < allIds.length

  const handleSelectAll = (checked: boolean) => {
    onSelectionChange(checked ? allIds : [])
  }

  const handleSelectRow = (resID: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedIds, resID])
    } else {
      onSelectionChange(selectedIds.filter((id) => id !== resID))
    }
  }

  const onRelease = useCallback(
    async (instance: AdminInstance) => {
      const teamResID = teamStore.teamTokenData?.teamResID
      if (!teamResID) return

      await Modal.confirm({
        title: t('instance.releaseModal.title'),
        content: (
          <div className="bg-panel-gray-100 rounded-lg p-4 flex-row mb-4">
            <div>
              <div className="text-[20px] leading-7 font-bold text-primary">
                {instance.displayTitle}
              </div>
            </div>
            <Divider className="my-3" />
            <div>
              <div className="t-sb text-secondary font-medium">
                <Trans i18nKey="instance.releaseModal.content">
                  The machine will be stopped and released, please save the environment if you need
                  it.
                </Trans>
              </div>
              <div
                className="text-btn-secondary t-sb cursor-pointer"
                onClick={() =>
                  window.open('https://docs.glows.ai/docs/my-instance#1-take-snapshot', '_blank')
                }
              >
                <Trans i18nKey="instance.releaseModal.whySaveEnvironment">
                  Why save the environment?
                </Trans>
              </div>
            </div>
          </div>
        ),
        confirmBtnCfg: {
          text: t('instance.releaseModal.stopReleaseBtn'),
        },
        className: 'w-[800px]',
      })

      const res = await API.deleteTeamMemberInstance({
        res_id: teamResID,
        inst_res_id: instance.resID,
      })

      if (res.code === 0) {
        message.success(t('common.message.success'))
        onUpdate()
      }
    },
    [teamStore.teamTokenData?.teamResID, t, onUpdate],
  )

  const columns: ColumnsType<AdminInstance> = [
    {
      title: (
        <Checkbox
          checked={isAllSelected}
          onChange={handleSelectAll}
          className={cn({ 'after:opacity-50': isSomeSelected && !isAllSelected })}
        />
      ),
      key: 'checkbox',
      width: 60,
      fixed: 'left',
      render: (_, record) => (
        <Checkbox
          checked={selectedIds.includes(record.resID)}
          onChange={(checked) => handleSelectRow(record.resID, checked)}
        />
      ),
    },
    {
      title: <Trans i18nKey="instance.tableHeaderID">ID</Trans>,
      dataIndex: 'resID',
      key: 'resID',
      width: 150,
      className: 't-sm',
      fixed: 'left',
    },
    {
      title: <Trans i18nKey="instance.tableHeaderName">Name</Trans>,
      dataIndex: 'displayTitle',
      key: 'displayTitle',
      className: 't-sm',
      width: 300,
      ellipsis: true,
    },
    {
      title: <Trans i18nKey="team.admin.table.region">Region</Trans>,
      key: 'domain',
      width: 120,
      className: 't-sm',
      render: (_, record) =>
        domainInfos.find((info) => info.domain === record.domain)?.displayName ||
        formatDomain(record.domain),
    },
    {
      title: <Trans i18nKey="team.admin.table.owner">Owner</Trans>,
      key: 'owner',
      width: 150,
      className: 't-sm',
      render: (_, record) => record.owner.nickname || record.owner.name,
    },
    {
      title: <Trans i18nKey="team.admin.table.status">Status</Trans>,
      key: 'status',
      width: 120,
      render: (_, record) => (
        <span className={cn('t-sm', record.statusTextStyle)}>
          {t(record.statusKey, record.statusText)}
        </span>
      ),
    },
    {
      title: <Trans i18nKey="instance.tableHeaderCost">Cost</Trans>,
      dataIndex: 'cost',
      key: 'cost',
      width: 150,
      className: 't-sm',
      fixed: 'right',
      sorter: (a, b) => a.cost - b.cost,
      sortOrder: sortedInfo.columnKey === 'cost' ? sortedInfo.order : null,
      render: (cost) => formatPrice(cost, false),
    },
    {
      title: <Trans i18nKey="instance.tableHeaderAction">Action</Trans>,
      key: 'action',
      width: 150,
      className: 't-sm',
      fixed: 'right',
      render: (_, record) => (
        <div className="flex gap-3 items-center">
          {/*<Link
            to={`/team/instances/detail/${record.resID}`}
            state={{
              instanceData: record._fullData?.instance,
              memberData: record._fullData?.user,
            }}
            className="text-btn-secondary cursor-pointer t-sb"
          >
            <Trans i18nKey="team.admin.table.details">Details</Trans>
          </Link>*/}
          <ActionMenu
            items={[
              {
                label: <Trans i18nKey="instance.list.action.release">Release</Trans>,
                onClick: () => onRelease(record),
                type: 'danger' as const,
                disabled: !record.canRelease,
              },
            ]}
            popupContainer="body"
          />
        </div>
      ),
    },
  ]

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            headerBg: 'rgb(var(--color-panel-white))',
            headerColor: 'rgb(var(--color-text-secondary))',
            rowHoverBg: 'rgb(var(--color-table-row-selected))',
          },
        },
      }}
    >
      <Table
        rowKey="resID"
        columns={columns}
        dataSource={instances}
        pagination={false}
        scroll={{ x: 'max-content' }}
        onChange={(_pagination, _filters, sorter: any) => setSortedInfo(sorter)}
        className="admin-instance-table"
      />
    </ConfigProvider>
  )
}
